import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Sheet } from "@cntrl/ui";
import { isNative } from "@cntrl/native";
import { useProfilePanel } from "../../app/profilePanel.js";
import { useMessagePanel } from "../../app/messagePanel.js";
import { CrewProfileModal } from "./CrewProfileModal.js";
import { MobileContactProfileScreen } from "./MobileContactProfileScreen.js";
import styles from "./ProfileHost.module.scss";
import { usePhoneLayout } from "../../app/phoneLayout.js";

/**
 * THE ONE PERSON PROFILE, opened from anywhere (ART-264). Mounted once per authed layout; draws whatever
 * `openPersonProfile` asked for — `CrewProfileModal` on desktop, `ProfileSheet` on a phone — over the current
 * screen, so a chat stays where it was underneath and closing the profile returns to it.
 *
 * **It sits at the DIALOG layer and is portalled to `<body>`**, because the surfaces that open it are themselves
 * overlays: the desktop inbox and the mobile chat takeover are at the modal layer, and a chat's member list is a
 * dialog. Mounted inside the shell at the modal layer it would open UNDER the chat that asked for it.
 *
 * **It closes when the route changes or a conversation is requested** — the profile's own Message button and any
 * band tile leave this screen, and a profile left open over the destination would be a dialog nobody asked for.
 */
export function ProfileHost({
  pathname,
  onOpenBand,
}: {
  /** The current route — a change closes the profile. Passed in, so the host stays render-context free. */
  pathname: string;
  onOpenBand?: (bandId: string) => void;
}) {
  const personId = useProfilePanel((s) => s.personId);
  const close = useProfilePanel((s) => s.close);
  const wide = !usePhoneLayout();
  const mobile = isNative() || !wide;

  useEffect(() => close(), [pathname, close]);
  const messageRequested = useMessagePanel((s) => s.personId);
  useEffect(() => {
    if (messageRequested) close();
  }, [messageRequested, close]);

  if (!personId) return null;
  return mobile ? (
    <ProfileSheet personId={personId} onClose={close} onOpenBand={onOpenBand} />
  ) : (
    createPortal(
      <div className={styles.raised}>
        <CrewProfileModal personId={personId} onClose={close} />
      </div>,
      document.body,
    )
  );
}

/** The phone's person profile as a bottom sheet — the same body as the `/contacts/person` screen. */
export function ProfileSheet({
  personId,
  onClose,
  onOpenBand,
}: {
  personId: string;
  onClose: () => void;
  onOpenBand?: (bandId: string) => void;
}) {
  return (
    <Sheet title="Profile" onClose={onClose} closeLabel="Close profile" className={styles.sheet} backdropClassName={styles.raisedSheet}>
      <MobileContactProfileScreen personId={personId} onOpenBand={onOpenBand} embedded />
    </Sheet>
  );
}
